import { ChevronDown, ChevronUp } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib/cn";
import { TableHead } from "@/shared/ui/table";

export type SortDirection = "asc" | "desc";

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

export function nextSortState<K extends string>(
  current: SortState<K>,
  key: K,
): SortState<K> {
  return {
    key,
    direction:
      current.key === key && current.direction === "desc" ? "asc" : "desc",
  };
}

export function sortRows<T, K extends string>(
  rows: readonly T[],
  sortState: SortState<K>,
  getValue: (row: T, key: K) => number | string | null | undefined,
): T[] {
  return [...rows].sort((left, right) => {
    const leftValue = getValue(left, sortState.key);
    const rightValue = getValue(right, sortState.key);
    const leftMissing = isMissing(leftValue);
    const rightMissing = isMissing(rightValue);

    if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
    if (leftMissing) return 0;

    const comparison =
      typeof leftValue === "number" && typeof rightValue === "number"
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue));
    return sortState.direction === "asc" ? comparison : -comparison;
  });
}

export function SortableTableHead<K extends string>({
  sortKey,
  sortState,
  onSort,
  children,
  className,
}: {
  sortKey: K;
  sortState: SortState<K>;
  onSort: (key: K) => void;
  children: ReactNode;
  className?: string | undefined;
}) {
  const active = sortState.key === sortKey;
  const direction = active ? sortState.direction : undefined;

  return (
    <TableHead
      scope="col"
      aria-sort={
        direction === "asc"
          ? "ascending"
          : direction === "desc"
            ? "descending"
            : "none"
      }
      className={className}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex min-h-7 items-center gap-1 rounded px-1 text-inherit transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70",
          active && "bg-surface-raised",
        )}
      >
        {children}
        {direction === "asc" ? (
          <ChevronUp className="size-3" aria-hidden="true" />
        ) : direction === "desc" ? (
          <ChevronDown className="size-3" aria-hidden="true" />
        ) : null}
      </button>
    </TableHead>
  );
}

function isMissing(value: number | string | null | undefined): boolean {
  return (
    value == null || (typeof value === "number" && !Number.isFinite(value))
  );
}
